import {observer} from "mobx-react";
import {useContext, useState} from "react";
import {IconButton, TextField, Typography, Button} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {Frame} from "../../common/Frame";
import {LessonEditorStoreContext} from "../../../stores/LessonEditorStore";
import {AppContext} from "../../../stores/AppStore";
import {lessons} from "../../../proto/compiled";
import {ContentBlockEditor} from "./ContentBlockEditor";

interface ChapterEditorProps {
  chapter: lessons.ILessonChapter;
}

export const ChapterEditor = observer(({chapter}: ChapterEditorProps) => {
  const store = useContext(LessonEditorStoreContext);
  const app = useContext(AppContext);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(chapter.title ?? "");

  if (!store || !app) return null;

  const isAnyBlockEditing = !!store.editingBlockId;

  const handleSaveTitle = async () => {
    if (chapter.id) {
      await store.updateChapter(chapter.id, titleValue);
    }
    setEditingTitle(false);
  };

  const handleCancelEdit = () => {
    setTitleValue(chapter.title ?? "");
    setEditingTitle(false);
  };

  const handleDelete = () => {
    app.confirmationDialogAction = async () => {
      if (chapter.id) {
        await store.deleteChapter(chapter.id);
      }
    };
    app.confirmationDialogText = `Are you sure you want to delete "${chapter.title}"? This will delete all content blocks in this chapter.`;
  };

  const handleAddTextBlock = async (afterBlockId?: string) => {
    if (chapter.id) {
      await store.createContentBlock(
        chapter.id,
        lessons.ContentBlockType.CONTENT_BLOCK_TEXT,
        afterBlockId
      );
    }
  };

  const handleAddAnnotationBlock = async (afterBlockId?: string) => {
    if (chapter.id) {
      const block = await store.createContentBlock(
        chapter.id,
        lessons.ContentBlockType.CONTENT_BLOCK_ANNOTATION,
        afterBlockId
      );
      if (block?.id) {
        store.openAnnotationSelector(block.id);
      }
    }
  };

  return (
    <Frame
      direction="column"
      noTransition
      gap={2}
      padding={3}
      radius={1.5}
      appearance="card"
    >
      {/* Chapter Header */}
      <Frame noTransition row alignItems="center" justifyContent="space-between" gap={2}>
        {editingTitle ? (
          <Frame noTransition row alignItems="center" gap={1} grow>
            <TextField
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveTitle();
                if (e.key === 'Escape') handleCancelEdit();
              }}
            />
            <IconButton size="small" onClick={handleSaveTitle} color="primary">
              <SaveIcon fontSize="small"/>
            </IconButton>
            <IconButton size="small" onClick={handleCancelEdit}>
              <CancelIcon fontSize="small"/>
            </IconButton>
          </Frame>
        ) : (
          <Frame row noTransition alignItems="center" gap={1} grow>
            <Typography variant="h6" fontWeight={600}>
              {chapter.title}
            </Typography>
            <IconButton size="small" onClick={() => setEditingTitle(true)}>
              <EditIcon fontSize="small"/>
            </IconButton>
          </Frame>
        )}

        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteIcon/>}
          onClick={handleDelete}
        >
          Delete Chapter
        </Button>
      </Frame>

      {/* Content Blocks */}
      <Frame noTransition direction="column" gap={1}>
        {chapter.contentBlocks?.map((block) => (
          <Frame key={block.id} direction="column">
            <ContentBlockEditor block={block} chapterId={chapter.id!} />

            {/* Insert buttons between blocks */}
            <AddBlockButtons
              onAddText={() => handleAddTextBlock(block.id ?? undefined)}
              onAddAnnotation={() => handleAddAnnotationBlock(block.id ?? undefined)}
              disabled={isAnyBlockEditing}
            />
          </Frame>
        ))}

        {/* Add block buttons at the end */}
        {(!chapter.contentBlocks || chapter.contentBlocks.length === 0) && (
          <AddBlockButtons
            onAddText={() => handleAddTextBlock(undefined)}
            onAddAnnotation={() => handleAddAnnotationBlock(undefined)}
            disabled={isAnyBlockEditing}
          />
        )}
      </Frame>
    </Frame>
  );
});

interface AddBlockButtonsProps {
  onAddText: () => void;
  onAddAnnotation: () => void;
  disabled?: boolean;
}

const AddBlockButtons = ({onAddText, onAddAnnotation, disabled}: AddBlockButtonsProps) => {
  const theme = useTheme();
  return (
    <Frame
      row
      alignItems="center"
      paddingV={0.5}
      center
      gap={3}
      marginTop={1}
      marginBottom={1}
      radius={0}
      style={{
        backgroundColor: theme.palette.shade3,
        marginLeft: -24,
        marginRight: -24,
      }}
    >
      <Button
        variant="text"
        size="small"
        startIcon={<TextFieldsIcon/>}
        onClick={onAddText}
        disabled={disabled}
        sx={{textTransform: 'none'}}
      >
        Add text
      </Button>
      <Button
        variant="text"
        size="small"
        startIcon={<VideoLibraryIcon/>}
        onClick={onAddAnnotation}
        disabled={disabled}
        sx={{textTransform: 'none'}}
      >
        Add video example
      </Button>
    </Frame>
  );
};
